import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import {
  CONSULTATION_CHANNELS,
  CONSULTATION_COUNTERPARTS,
  CONSULTATION_TOPICS,
  type ConsultationChannel,
  type ConsultationCounterpart,
  type ConsultationTopic,
  type LearnerConsultationView,
} from '@/api/learnerConsultations'
import {
  useDeleteLearnerConsultation,
  useLearnerConsultations,
  useSaveLearnerConsultation,
} from '@/hooks/admin/useLearnerConsultations'
import { formatKstDateTime, kstDateTimeInputToIso, toKstDateTimeInputValue } from '@/utils/kstDateTimeInput'

const MAX_CONTENT_LENGTH = 5000

interface Props {
  userId: number
}

interface FormState {
  consultationId: number | null
  consultedAt: string
  channel: ConsultationChannel
  counterpart: ConsultationCounterpart
  topic: ConsultationTopic
  content: string
}

function emptyForm(): FormState {
  return {
    consultationId: null,
    consultedAt: toKstDateTimeInputValue(new Date()),
    channel: 'PHONE',
    counterpart: 'GUARDIAN',
    topic: 'LEARNING',
    content: '',
  }
}

function formFrom(item: LearnerConsultationView): FormState {
  return {
    consultationId: item.consultationId,
    consultedAt: toKstDateTimeInputValue(new Date(item.consultedAt)),
    channel: item.channel,
    counterpart: item.counterpart,
    topic: item.topic,
    content: item.content,
  }
}

/** 원생별 상담 기록 — 원생 상세 탭·원생 목록 모달 공용. 작성자 구분 없이 누구나 수정·삭제한다. */
export function LearnerConsultationPanel({ userId }: Props) {
  const { t, i18n } = useTranslation(['admin', 'common'])
  const listQ = useLearnerConsultations(userId)
  const saveM = useSaveLearnerConsultation(userId)
  const deleteM = useDeleteLearnerConsultation(userId)
  const [form, setForm] = useState<FormState | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const contentInputId = useId()

  const items = listQ.data ?? []
  const consultedAtIso = form ? kstDateTimeInputToIso(form.consultedAt) : null
  const canSubmit = form != null && consultedAtIso != null && form.content.trim().length > 0 && !saveM.isPending

  const submit = () => {
    if (!form || !consultedAtIso) return
    saveM.mutate(
      {
        consultationId: form.consultationId,
        body: {
          consultedAt: consultedAtIso,
          channel: form.channel,
          counterpart: form.counterpart,
          topic: form.topic,
          content: form.content.trim(),
        },
      },
      { onSuccess: () => setForm(null) },
    )
  }

  const remove = (consultationId: number) => {
    deleteM.mutate(consultationId, { onSuccess: () => setConfirmDeleteId(null) })
  }

  const selectClass = 'rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500'

  return (
    <div className="space-y-4" data-testid="learner-consultation-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          {t('admin:students.consultation.count', { count: items.length })}
        </h3>
        {form == null && (
          <button
            type="button"
            onClick={() => { setConfirmDeleteId(null); setForm(emptyForm()) }}
            className="inline-flex items-center rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700"
          >
            {t('admin:students.consultation.add')}
          </button>
        )}
      </div>

      {form != null && (
        <form
          className="space-y-3 rounded-lg border border-primary-200 bg-primary-50/40 p-4"
          onSubmit={(e) => { e.preventDefault(); submit() }}
          aria-label={t(form.consultationId == null ? 'admin:students.consultation.add' : 'admin:students.consultation.edit')}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              {t('admin:students.consultation.field.consultedAt')}
              <input
                type="datetime-local"
                value={form.consultedAt}
                onChange={(e) => setForm({ ...form, consultedAt: e.target.value })}
                className={selectClass}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              {t('admin:students.consultation.field.channel')}
              <select
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value as ConsultationChannel })}
                className={selectClass}
              >
                {CONSULTATION_CHANNELS.map((v) => (
                  <option key={v} value={v}>{t(`admin:students.consultation.channel.${v}`)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              {t('admin:students.consultation.field.counterpart')}
              <select
                value={form.counterpart}
                onChange={(e) => setForm({ ...form, counterpart: e.target.value as ConsultationCounterpart })}
                className={selectClass}
              >
                {CONSULTATION_COUNTERPARTS.map((v) => (
                  <option key={v} value={v}>{t(`admin:students.consultation.counterpart.${v}`)}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-600">
              {t('admin:students.consultation.field.topic')}
              <select
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value as ConsultationTopic })}
                className={selectClass}
              >
                {CONSULTATION_TOPICS.map((v) => (
                  <option key={v} value={v}>{t(`admin:students.consultation.topic.${v}`)}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-col gap-1 text-xs text-gray-600">
            <label htmlFor={contentInputId}>{t('admin:students.consultation.field.content')}</label>
            <textarea
              id={contentInputId}
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              maxLength={MAX_CONTENT_LENGTH}
              rows={5}
              placeholder={t('admin:students.consultation.contentPlaceholder')}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <span className="self-end text-gray-400">{form.content.length} / {MAX_CONTENT_LENGTH}</span>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setForm(null)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('common:button.save')}
            </button>
          </div>
        </form>
      )}

      {listQ.isLoading && (
        <p className="py-6 text-center text-sm text-gray-500" role="status">{t('common:label.loading')}</p>
      )}
      {listQ.isError && (
        <p className="py-6 text-center text-sm text-red-600" role="alert">{t('admin:students.consultation.loadError')}</p>
      )}
      {!listQ.isLoading && !listQ.isError && items.length === 0 && (
        <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-10 text-center text-sm text-gray-500">
          {t('admin:students.consultation.empty')}
        </p>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {items.map((item) => (
            <li key={item.consultationId} className={clsx('p-4', form?.consultationId === item.consultationId && 'bg-primary-50/40')}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {formatKstDateTime(item.consultedAt, i18n.language)}
                  </span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                    {t(`admin:students.consultation.channel.${item.channel}`)}
                  </span>
                  <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">
                    {t(`admin:students.consultation.counterpart.${item.counterpart}`)}
                  </span>
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700">
                    {t(`admin:students.consultation.topic.${item.topic}`)}
                  </span>
                </div>
                {confirmDeleteId === item.consultationId ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">{t('admin:students.consultation.deleteConfirm')}</span>
                    <button
                      type="button"
                      onClick={() => remove(item.consultationId)}
                      disabled={deleteM.isPending}
                      className="rounded-lg bg-red-600 px-2.5 py-1 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      {t('common:button.delete')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded-lg border border-gray-200 px-2.5 py-1 text-gray-700 hover:bg-gray-50"
                    >
                      {t('common:button.cancel')}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-sm">
                    <button
                      type="button"
                      onClick={() => { setConfirmDeleteId(null); setForm(formFrom(item)) }}
                      className="rounded-lg px-2.5 py-1 text-gray-600 hover:bg-gray-100"
                    >
                      {t('admin:students.consultation.edit')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(item.consultationId)}
                      className="rounded-lg px-2.5 py-1 text-red-600 hover:bg-red-50"
                    >
                      {t('common:button.delete')}
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-800">{item.content}</p>
              <p className="mt-2 text-xs text-gray-400">
                {t('admin:students.consultation.authoredBy', { name: item.authorName ?? '-' })}
                {item.updatedAt && (
                  <>
                    {' · '}
                    {t('admin:students.consultation.updatedBy', {
                      name: item.updatedByName ?? '-',
                      at: formatKstDateTime(item.updatedAt, i18n.language),
                    })}
                  </>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
